import React, { useCallback } from "react";
import { useTranslation } from "react-i18next"; // Import useTranslation
import { toast } from "react-toastify";
import  cn from "classnames";
import style from "../common.module.scss";
import LineIcon from "../../../shared/icons/LineIcon";
import LineFlexIcon from "../../../shared/icons/LineFlexIcon";
import { getCountryIcon } from "../../../../utils/getCountryIcon";

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const OrdersInfoAddressList = ({ addressList, order, isMarketplacePage = false }) => {
  const { t } = useTranslation();

  const copyAddress = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("orderInfo.ordersInfoAddressList.addressCopied"));
    } catch {
      toast.error(t("orderInfo.ordersInfoAddressList.copyFailed"));
    }
  };

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);

    const day = date.getDate();
    const monthIndex = date.getMonth();
    const weekdayIndex = date.getDay();

    const monthKeys = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const weekdayKeys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

    const monthKey = monthKeys[monthIndex];
    const weekdayKey = weekdayKeys[weekdayIndex];

    const month = t(`common.datetime.short_months.${monthKey}`);
    const weekday = t(`common.datetime.short_weekdays.${weekdayKey}`);

    return `${day} ${month}, ${weekday}`;
  }, [t]);


  const formatTime = useCallback((hour) => {
    return `${hour.toString().padStart(2, "0")}:00`;
  }, []);

  const baseHeight = 60;
  const lineHeight = baseHeight * addressList?.length;

  return (
    <div className={cn(style.shipping_wrapper)}>
      {addressList && addressList.length > 2 ? (
        <LineFlexIcon height={lineHeight} />
      ) : (
        <LineIcon />
      )}
      <div className={cn(style.shipping_content, {
          [style.shipping_content__twoColumns]: isMarketplacePage,
      })}>
        {addressList &&
          addressList.map((address, index) => {
            const isStart = address.step === 1;
            const isEnd = index === addressList.length - 1;
            const addressText = [address.cityName, address.address].filter(Boolean).join(", ");

            let pointTypeKey = 'orderInfo.ordersInfoAddressList.pointTypeIntermediate';
            if (isStart) pointTypeKey = 'orderInfo.ordersInfoAddressList.pointTypeFrom';
            if (isEnd) pointTypeKey = 'orderInfo.ordersInfoAddressList.pointTypeTo';
            const pointType = t(pointTypeKey);

            let recipientTypeKey = 'orderInfo.ordersInfoAddressList.recipientTypeRecipient';
            if (address.type === "Loading") recipientTypeKey = 'orderInfo.ordersInfoAddressList.recipientTypeSender';
            const recipientType = t(recipientTypeKey);

            const useExpectedDate = !!address.expectedUnloadingDate;
            const displayDate = useExpectedDate ? address.expectedUnloadingDate : address.shippingDate;

            const showDateRange = order?.isComplexTender && isStart && !useExpectedDate && hasValue(order?.shippingDateTo) && displayDate;
            const dateText = showDateRange
              ? (
                <>
                  {formatDate(displayDate)} -
                  <br />
                  {formatDate(order.shippingDateTo)}
                </>
              )
              : formatDate(displayDate);

            return (
              <React.Fragment key={address.id || index}>
                <div className={style.shipping_column}>
                  <span className={style.shipping_column_head}>
                    {address.type === "Loading" ? t('orderInfo.ordersInfoAddressList.typeLoading') : t('orderInfo.ordersInfoAddressList.typeUnloading')}
                  </span>
                  <div className={style.shipping_column_text}>
                    {displayDate ? dateText : <span className={style.shipping_column_awaiting}>{t("orderInfo.ordersInfoDirections.awaitingDateAssignment")}</span>}
                  </div>
                  {useExpectedDate ? (
                    <div className={style.shipping_column_footer}>{t('orderInfo.ordersInfoAddressList.estimatedDate')}</div>
                  ) : (!useExpectedDate && address?.shippingDate && hasValue(address?.shippingHourFrom) && hasValue(address?.shippingHourTo) && !(address?.shippingHourFrom === 0 && address?.shippingHourTo === 0) && (
                    <div className={style.shipping_column_footer}>
                      {t('orderInfo.ordersInfoAddressList.timeFrom')} {formatTime(address?.shippingHourFrom)} {t('orderInfo.ordersInfoAddressList.timeTo')}{" "}
                      {formatTime(address?.shippingHourTo)}
                    </div>
                  ))}
                </div>

                <div
                  className={cn(style.shipping_column, style.shipping_column_copy)}
                  role="button"
                  tabIndex={addressText ? 0 : -1}
                  aria-disabled={!addressText}
                  aria-label={`${t("orderInfo.ordersInfoAddressList.copyAddress")}: ${addressText}`}
                  title={t("orderInfo.ordersInfoAddressList.copyAddress")}
                  onClick={() => copyAddress(addressText)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      copyAddress(addressText);
                    }
                  }}
                >
                  <span className={style.shipping_column_head}>{pointType}</span>
                  <div className={style.shipping_column_main}>
                    <img
                      src={getCountryIcon(address?.countryId)}
                      className={style.shipping_column_countryIcon}
                      alt={t('orderInfo.ordersInfoAddressList.countryAlt')}
                    />
                    <div className={style.shipping_column_text}>{address?.cityName}</div>
                  </div>
                  <div className={style.shipping_column_footer}>{!isMarketplacePage && address?.address}</div>
                </div>

                {!isMarketplacePage && (
                  <div className={style.shipping_column}>
                    <span className={style.shipping_column_head}>{recipientType}</span>
                    <div className={style.shipping_column_text}>
                      {address.type === "Loading" ? address?.senderName : address?.recipientName}
                    </div>
                    <span className={style.shipping_column_footer}>
                      {address.type === "Loading" ? address?.senderPhoneNumber : address?.recipientPhoneNumber}
                    </span>
                  </div>
                )}
              </React.Fragment>
            );
          })}
      </div>
    </div>
  );
};

export default OrdersInfoAddressList;
